<script lang="ts">
export default { inheritAttrs: false };
</script>

<script lang="ts" setup>
import { ButtonHTMLAttributes, computed, useAttrs, type Component } from 'vue';

const props = defineProps<{
  icon?: Component;
  type?: ButtonHTMLAttributes['type'];
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  /** Tooltip text shown on hover; when omitted, no tooltip is rendered */
  label?: string;
}>();

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const attrs = useAttrs();

const isDisabled = computed(() => props.disabled || props.loading);

const computedAriaLabel = computed(() => props.ariaLabel || props.label);

const filteredAttrs = computed(() => {
  const { disabled, type, ...rest } = attrs;
  return rest;
});

const handleClick = (e: MouseEvent) => {
  if (isDisabled.value) return;
  emit('click', e);
};
</script>

<template>
  <div class="tw:relative tw:inline-block" :class="{ group: Boolean(label) }">
    <button
      :type="props.type || 'button'"
      :disabled="isDisabled"
      :aria-label="computedAriaLabel"
      :aria-disabled="isDisabled || undefined"
      v-bind="filteredAttrs"
      class="tw:p-2 tw:bg-general tw:text-ghost tw:rounded-lg tw:overflow-hidden tw:not-disabled:active:text-secondary tw:lg:not-disabled:hover:text-secondary tw:disabled:opacity-50 tw:disabled:cursor-not-allowed tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-main-blue"
      @click="handleClick"
    >
      <div class="tw:w-7 tw:h-7 tw:flex tw:items-center tw:justify-center">
        <template v-if="loading">
          <span class="tw:sr-only">Loading</span>
          <div
            class="tw:w-4 tw:h-4 tw:rounded-full tw:border-2 tw:border-current tw:border-t-transparent tw:animate-spin"
          />
        </template>
        <component v-else-if="icon" :is="icon" />
        <slot v-else />
      </div>
    </button>
    <div
      v-if="label"
      class="tw:absolute tw:bottom-full tw:left-1/2 tw:-translate-x-1/2 tw:mb-2 tw:px-2 tw:py-1 tw:rounded tw:bg-secondary tw:text-white tw:typography-small tw:whitespace-nowrap tw:opacity-0 group-hover:tw:opacity-100 tw:pointer-events-none tw:transition-opacity"
    >
      {{ label }}
      <div
        class="tw:absolute tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:border-4 tw:border-transparent tw:border-t-secondary"
      />
    </div>
  </div>
</template>
